import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { StorageService } from '../services/storage/storage.service';
import { Router } from '@angular/router';

/**
 * Auth interceptor that:
 * 1. Checks token validity BEFORE making requests (like the old authentication.service.ts)
 * 2. Automatically refreshes expired tokens before making requests
 * 3. Handles 401 errors by attempting token refresh and retrying the request
 * 4. Only redirects to sign-in if token refresh fails
 *
 * Based on the old authentication.service.ts getBearer() method pattern
 */
export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const storageService = inject(StorageService);
  const router = inject(Router);

  // Skip adding token to authentication endpoints (including refresh_token)
  const url = req.url;
  if (isAuthEndpoint(url)) {
    return next(req);
  }

  // Get bearer token (this will check validity and refresh if needed BEFORE making the request)
  // This matches the old authentication.service.ts pattern where getBearer() checks validity first
  return from(authService.getBearer()).pipe(
    switchMap((bearerToken) => {
      // If getBearer() returned null, it means no valid token and refresh failed
      // Proceed without token - this will likely result in a 401
      if (!bearerToken) {
        // Check if we have a refresh token - if not, clear auth immediately
        const refreshToken = authService.authState().refreshToken || storageService.getItem('refreshToken');
        if (!refreshToken) {
          authService.signOut('auth.interceptor.ts:42 - No refresh token available', router.url || req.url);
        }
        // Proceed without token - will get 401 which will be handled below
        return next(req);
      }

      // Add token to request
      const clonedReq = addTokenToRequest(req, bearerToken.Authorization.replace('Bearer ', ''));

      // Make the request
      return next(clonedReq).pipe(
        catchError((err: HttpErrorResponse) => {
          // If we get a 401, try to refresh the token and retry
          // This handles cases where token expired between getBearer() and the actual request
          if (err.status === 401 && !isAuthEndpoint(url)) {
            const refreshToken = authService.authState().refreshToken || storageService.getItem('refreshToken');

            if (refreshToken) {
              // Attempt to refresh the token
              return from(authService.refreshAccessToken()).pipe(
                switchMap((newAccessToken) => {
                  if (newAccessToken) {
                    // Token refreshed successfully, retry the original request with new token
                    const retryReq = addTokenToRequest(req, newAccessToken);
                    return next(retryReq);
                  } else {
                    // Refresh failed, clear auth and redirect
                    authService.signOut('auth.interceptor.ts:70 - Token refresh failed after 401', router.url || req.url);
                    return throwError(() => new Error('Token refresh failed'));
                  }
                }),
                catchError((refreshError) => {
                  // Refresh failed, clear auth and redirect
                  authService.signOut('auth.interceptor.ts:77 - Token refresh error handler', router.url || req.url);
                  return throwError(() => refreshError || new Error('Token refresh failed'));
                }),
              );
            }
            // No refresh token available, clear auth and redirect
            authService.signOut('auth.interceptor.ts:84 - No refresh token available after 401', router.url || req.url);
            return throwError(() => err);
          }

          // Re-throw the error if it's not a 401 or we can't handle it
          return throwError(() => err);
        }),
      );
    }),
    catchError((error) => {
      // If getBearer() throws an error, proceed without token
      // This will likely result in a 401, which will be handled above
      return next(req).pipe(
        catchError((err: HttpErrorResponse) => {
          // If we get a 401 and no refresh token, clear auth and redirect
          if (err.status === 401 && !isAuthEndpoint(url)) {
            const refreshToken = authService.authState().refreshToken || storageService.getItem('refreshToken');
            if (!refreshToken) {
              authService.signOut('auth.interceptor.ts:104 - No refresh token in error handler', router.url || req.url);
            }
            return throwError(() => err);
          }
          return throwError(() => err);
        }),
      );
    }),
  );
}

/**
 * Helper function to check if URL is an authentication endpoint
 * Note: subscribe_notification requires auth, so it's NOT excluded
 */
function isAuthEndpoint(url: string): boolean {
  // Exclude authentication endpoints that don't require auth
  if (url.includes('/subscribe_notification')) {
    return false; // This endpoint requires auth
  }
  return (
    url.includes('/authentication/sign_in') ||
    url.includes('/authentication/sign_up') ||
    url.includes('/authentication/validation_email') ||
    url.includes('/authentication/refresh_token') ||
    url.includes('/authentication/request_password_reset') ||
    url.includes('/authentication/reset_password') ||
    url.includes('/authentication/validate_set_password_token') ||
    url.includes('/authentication/set_password_with_token') ||
    url.includes('/api/authentication/sign_in') ||
    url.includes('/api/authentication/sign_up') ||
    url.includes('/api/authentication/validation_email') ||
    url.includes('/api/authentication/refresh_token') ||
    url.includes('/api/authentication/request_password_reset') ||
    url.includes('/api/authentication/reset_password') ||
    url.includes('/api/authentication/validate_set_password_token') ||
    url.includes('/api/authentication/set_password_with_token') ||
    url.includes('/token') ||
    url.includes('/sign-in') ||
    url.includes('/sign-up')
  );
}

/**
 * Helper function to add token to request headers
 */
function addTokenToRequest(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  // If no token, return request as-is
  if (!token) {
    return req;
  }

  // If request already has Authorization header, don't override it
  if (req.headers.has('Authorization')) {
    return req;
  }

  // Add Authorization header
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}
